import { Injectable } from '@angular/core';
import { CoverageDisplayEnum } from '@app/core/enums/coverage-display.enum';
import {
    CoverageSpecificationInterface,
    PurchasedCoveragePremiumInterface,
    PurchasedProductInterface,
} from 'outshared-lib';
import { CoveragePremiumTotalInterface } from '../interfaces';

@Injectable({
    providedIn: 'root',
})
export class CoveragePriceService {
    public mergePremium(
        coverage: CoverageSpecificationInterface,
        baseCoverage: CoverageSpecificationInterface
    ): CoverageSpecificationInterface {
        const newCoverage: CoverageSpecificationInterface = Object.assign({}, coverage);

        newCoverage.premium = this.mergePriceObject(coverage.premium, baseCoverage.premium);

        newCoverage.premiumIncludingDiscount = this.mergePriceObject(
            coverage.premiumIncludingDiscount,
            baseCoverage.premiumIncludingDiscount
        );

        if (coverage.collectivityDiscount) {
            newCoverage.collectivityDiscount = this.mergePriceObject(
                coverage.collectivityDiscount,
                baseCoverage.collectivityDiscount
            );
        }

        return newCoverage;
    }

    public calculateCoveragePremiumTotal(
        purchasedProduct: PurchasedProductInterface,
        display: CoverageDisplayEnum
    ): CoveragePremiumTotalInterface {
        const coverages: CoverageSpecificationInterface[] = [
            ...purchasedProduct.coverage.baseCoverages,
            ...purchasedProduct.coverage.additionalCoverages,
        ];

        return display === CoverageDisplayEnum.Current
            ? this.calculateCurrentPremiumTotal(coverages)
            : this.calculcatFuturePremiumTotal(coverages);
    }

    private calculateCurrentPremiumTotal(coverages: CoverageSpecificationInterface[]): CoveragePremiumTotalInterface {
        const total = {
            premium: 0,
            premiumIncludingDiscount: 0,
            premiumIncludingStackDiscount: 0,
        };

        return coverages.reduce(
            (previousValue: CoveragePremiumTotalInterface, currentValue: CoverageSpecificationInterface) => {
                if (!currentValue.purchasedCoverage || !currentValue.premium) {
                    return previousValue;
                }

                return this.calculateTotal(previousValue, currentValue);
            },
            total
        );
    }

    private calculcatFuturePremiumTotal(coverages: CoverageSpecificationInterface[]): CoveragePremiumTotalInterface {
        const total: CoveragePremiumTotalInterface = {
            premium: 0,
            premiumIncludingDiscount: 0,
            premiumIncludingStackDiscount: 0,
        };

        return coverages.reduce(
            (previousValue: CoveragePremiumTotalInterface, currentValue: CoverageSpecificationInterface) => {
                const validEndDate = Boolean(currentValue.purchasedCoverage && currentValue.purchasedCoverage.endDate);
                const invalidPurchasedCoverage =
                    !currentValue.purchasedCoverage && !currentValue.purchasedCoverageFuture;
                const invalidPremium = !currentValue.premium;

                if (validEndDate || invalidPurchasedCoverage || invalidPremium) {
                    return previousValue;
                }

                return this.calculateTotal(previousValue, currentValue);
            },
            total
        );
    }

    private calculateTotal(
        previousValue: CoveragePremiumTotalInterface,
        currentValue: CoverageSpecificationInterface
    ): CoveragePremiumTotalInterface {
        const premium = currentValue.premium ? currentValue.premium.premiumIncludingPremiumTax : 0;
        const premiumIncludingDiscount = currentValue.premiumIncludingDiscount
            ? currentValue.premiumIncludingDiscount.premiumIncludingPremiumTax
            : 0;
        const premiumIncludingStackDiscount = currentValue.premiumIncludingStackDiscount
            ? currentValue.premiumIncludingStackDiscount.premiumIncludingPremiumTax
            : 0;

        return {
            premium: previousValue.premium + premium,
            premiumIncludingDiscount: previousValue.premiumIncludingDiscount + premiumIncludingDiscount,
            premiumIncludingStackDiscount: previousValue.premiumIncludingStackDiscount + premiumIncludingStackDiscount,
        };
    }

    private mergePriceObject(
        priceA: PurchasedCoveragePremiumInterface,
        priceB: PurchasedCoveragePremiumInterface
    ): PurchasedCoveragePremiumInterface {
        const mergedPrice: PurchasedCoveragePremiumInterface = {
            basicPremium: 0,
            premium: 0,
            premiumIncludingPremiumTax: 0,
            premiumTax: 0,
        };

        Object.keys(priceA).forEach((key) => {
            if (typeof priceA[key] === 'number') {
                mergedPrice[key] = priceA[key] + priceB[key];
            } else {
                mergedPrice[key] = priceA[key];
            }
        });

        return mergedPrice;
    }
}
